import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react';
import { twMerge } from 'tailwind-merge';
import { useDebounceCallback } from 'usehooks-ts';
import { InputAutoCompleteSimpleWithAccessorsI } from './input-autocomplete-simple-with-accessors.interface';
import InputAutocompleteSimpleLayout from '../input-autocomplete-simple-layout/input-autocomplete-simple-layout.component';
import { MIN_OPTION_HEIGHT, MIN_OPTION_VISIBLE } from '../input-autocomplete/input-autocomplete.constants';
import {
    filterQuery,
    handleAutocompleteRemoteQuery,
    renderOptionWithAccessor,
} from '../input-autocomplete/input-autocomplete.utils';

const MIN_GROUP_HEADER_HEIGHT = MIN_OPTION_HEIGHT;
export const InputAutoCompleteSimpleWithAccessors = <T extends Record<string, any>>({
    name,
    label,
    testId,
    value,
    options,
    isLoading = false,
    minVisible = MIN_OPTION_VISIBLE,
    optionDropdownClasses,
    optionClasses,
    containerClasses,
    inputClassName,
    enableEmptyOption = false,
    emptyOptionLabel,
    debounceTime = 3000,
    filterRemote,
    itemNotFoundError = false,
    itemNotFoundErrorMessage,
    accessorValue,
    accessorLabel,
    grouped = false,
    accessorGroup,
    groupedTitles,
    disabled,
    position,
    onChange,
    onInputChange,
    onScrollToEnd,
    callback,
    required,
    bypassGetLabelFromValue,
    onClick,
    onFocus,
    onBlur,
    ...props
}: InputAutoCompleteSimpleWithAccessorsI<T>) => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const calledQuery = useRef<string>('');

    const shouldUpdateValueInit = useRef<boolean>(true);

    // options change control
    const optionLengthControl = useRef<{ continue: boolean; length: number }>({
        continue: true,
        length: options.length,
    });

    const getLabelFromValue = useCallback(
        (value: string) => {
            if (bypassGetLabelFromValue) return value;
            // this function require options to be different of null
            // to return a value according key value, if options is
            // an async value you should handle an update with useEffect

            if ((!value || value === '') && enableEmptyOption) return '';

            // Check if value is in options
            const foundItem = options.find((item) => (item[accessorValue] as unknown as string) === value);

            if (!foundItem) {
                return '';
            }
            // Return label of found item
            return foundItem[accessorLabel] as unknown as string;
        },
        [accessorLabel, accessorValue, enableEmptyOption, options],
    );

    const [show, setShow] = useState(false);
    const [query, setQuery] = useState<string>(value ? getLabelFromValue(value) : '');
    const [valueSelected, setValueSelected] = useState<string | undefined>(value ? getLabelFromValue(value) : '');

    // Debounce query
    const debounced = useDebounceCallback(
        (value) => {
            if (value.length && options.length) {
                callback?.(value);
            }
        },
        debounceTime,
        { leading: true },
    );

    // Options handling empty option
    const filteredOptions = useMemo(() => {
        const optionsWithEmpty = enableEmptyOption
            ? [{ [accessorValue]: '', [accessorLabel]: emptyOptionLabel }, ...options]
            : options;

        return query !== valueSelected
            ? filterQuery(optionsWithEmpty, query, accessorLabel, accessorValue)
            : optionsWithEmpty;
    }, [accessorLabel, accessorValue, emptyOptionLabel, enableEmptyOption, valueSelected, options, query]);

    const processedOptions = useMemo(() => {
        if (!grouped || !accessorGroup) return filteredOptions;
        const groupableOptions = filteredOptions.filter(
            (option): option is T => (option as T)[accessorGroup] !== undefined,
        );
        const groups = groupableOptions.reduce((acc, option) => {
            const groupName = option[accessorGroup] as string;
            if (!acc[groupName]) {
                acc[groupName] = [];
            }
            acc[groupName].push(option as T);
            return acc;
        }, {} as Record<string, T[]>);

        const result: (T | { __groupHeader: true; groupName: string })[] = [];
        // Opcionalmente puedes ordenar los grupos
        Object.keys(groups).forEach((groupName) => {
            result.push({ __groupHeader: true, groupName }, ...groups[groupName]);
        });
        return result;
    }, [filteredOptions, grouped, accessorGroup]);

    const rowVirtualizer = useVirtualizer({
        count: processedOptions.length,
        getScrollElement: () => modalRef.current,
        estimateSize: (index) => {
            const item = processedOptions[index];
            // Si es header, usamos una altura distinta (o la misma, según lo requieras)
            if (typeof item === 'object' && item !== null && '__groupHeader' in item && item.__groupHeader) {
                return MIN_GROUP_HEADER_HEIGHT;
            }
            return MIN_OPTION_HEIGHT;
        },
    });

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newText = event.target.value;
        setQuery(newText);
        onInputChange?.(newText);

        // Si el input queda vacío y enableEmptyOption está activo, selecciona la opción vacía
        if (enableEmptyOption && newText === '') {
            setValueSelected(emptyOptionLabel || '');
            onChange?.('');
        }
    };

    const handleSelect = (value: string) => {
        const newValue = getLabelFromValue(value);
        setValueSelected(newValue);
        setQuery(newValue);
        setShow(false);
        onChange?.(value);
    };

    // handle trigger callback
    useEffect(() => {
        handleAutocompleteRemoteQuery({
            query,
            filteredOptionsLength: filteredOptions.length,
            isLoading,
            filterRemote,
            calledQueryRef: calledQuery,
            shouldOpenDropdown: filteredOptions.length > 0 && !show && query.length > 0 && valueSelected !== query,
            openDropdown: () => setShow(true),
            debounced,
        });
    }, [filteredOptions, valueSelected, filterRemote, debounced, isLoading, query, show]);

    // handle scroll end
    useEffect(() => {
        const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

        if (!lastItem) {
            return;
        }

        const isAtEndOfList = lastItem.index === processedOptions.length - 1;
        const optionLengthHasChanged = optionLengthControl.current.continue;
        const filteredGotQuery = query
            ? filteredOptions.some((item) =>
                  (item[accessorLabel] as string).toLocaleLowerCase().includes(query.toLocaleLowerCase()),
              )
            : false;
        const shouldTriggerOnScroll = isAtEndOfList && !isLoading && optionLengthHasChanged && !filteredGotQuery;

        if (shouldTriggerOnScroll) {
            onScrollToEnd?.();
            optionLengthControl.current = {
                ...optionLengthControl.current,
                continue: false,
            };
        } else if (!optionLengthControl.current.continue && optionLengthControl.current.length < options.length) {
            optionLengthControl.current = {
                continue: true,
                length: options.length,
            };
        }

        // Do NOT delete eslint bypass, change rowVirtualizer.getVirtualItems() to rowVirtualizer
        // it makes useeffect doesnt work properly
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        // eslint-disable-next-line react-hooks/exhaustive-deps
        rowVirtualizer.getVirtualItems(),
        filteredOptions,
        options,
        onScrollToEnd,
        isLoading,
    ]);

    useEffect(() => {
        if (options && options.length > 0 && value && value?.length > 0 && shouldUpdateValueInit.current) {
            const auxLabel = getLabelFromValue(value);
            const optionFounded = auxLabel !== '';
            setQuery(auxLabel);
            setValueSelected(auxLabel);
            shouldUpdateValueInit.current = !optionFounded;
        }
    }, [getLabelFromValue, options, value]);

    const optionsContainerClasses = twMerge(
        position === 'top'
            ? 'overflow-auto w-full shadow-lg border border-gray-50 rounded-t-lg bg-white absolute z-[50] bottom-full mb-1'
            : 'overflow-auto w-full shadow-lg border border-gray-50 rounded-b-lg bg-white relative z-[50]',
        show && processedOptions.length > 0 && 'py-2',
        optionDropdownClasses,
    );
    const optionItemClasses = twMerge(
        'flex justify-start items-center pl-2 font-light hover:bg-gray-50',
        optionClasses,
    );

    useEffect(() => {
        // Solo resetea si el value externo está vacío Y el usuario seleccionó la opción vacía
        if (
            value === '' &&
            enableEmptyOption &&
            valueSelected === (emptyOptionLabel || '') &&
            query === (emptyOptionLabel || '')
        ) {
            setQuery(emptyOptionLabel || '');
            setValueSelected(emptyOptionLabel || '');
        } else if (options && options.length > 0 && value && value?.length > 0 && shouldUpdateValueInit.current) {
            const auxLabel = getLabelFromValue(value);
            const optionFounded = auxLabel !== '';
            setQuery(auxLabel);
            setValueSelected(auxLabel);
            shouldUpdateValueInit.current = !optionFounded;
        }
    }, [getLabelFromValue, options, value, enableEmptyOption, emptyOptionLabel, valueSelected, query]);
    return (
        <InputAutocompleteSimpleLayout
            name={name}
            label={label}
            ref={modalRef}
            show={show}
            query={query}
            testId={testId || `${name}-autocomplete`}
            placeholder={emptyOptionLabel || props.placeholder}
            isLoading={isLoading}
            minVisible={minVisible}
            optionsLength={filteredOptions.length}
            optionsHeight={rowVirtualizer.getTotalSize()}
            handleShow={(value?: boolean) => setShow(value ?? ((prevState) => !prevState))}
            handleInputChange={handleInputChange}
            containerClasses={containerClasses}
            inputClassName={inputClassName}
            optionsContainerClasses={optionsContainerClasses}
            error={itemNotFoundError}
            errorMessage={itemNotFoundError ? itemNotFoundErrorMessage : ''}
            disabled={disabled}
            required={required}
            onClick={onClick}
            onFocus={onFocus}
            onBlur={onBlur}
            {...props}
        >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const item = processedOptions[virtualRow.index];
                if (typeof item === 'object' && item !== null && '__groupHeader' in item && item.__groupHeader) {
                    return (
                        <div
                            key={`group-${item.groupName}`}
                            ref={rowVirtualizer.measureElement}
                            data-index={virtualRow.index}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                minHeight: `${MIN_OPTION_HEIGHT}px`,
                                transform: `translateY(${virtualRow.start}px)`,
                            }}
                            className={clsx(
                                'font-semibold text-gray-300 px-2 py-1 flex items-center',
                                virtualRow.index !== 0 && 'border-t-2 border-gray-100 pt-3',
                            )}
                        >
                            {groupedTitles?.find((group) => group.id === item.groupName)?.title ||
                                `${item.groupName.toUpperCase()}:`}
                        </div>
                    );
                }
                return (
                    <Fragment key={String(virtualRow.key) || `option-key-${name}`}>
                        {renderOptionWithAccessor({
                            name,
                            item: item as T,
                            keyLabel: accessorLabel,
                            keyValue: accessorValue,
                            measureRef: rowVirtualizer.measureElement,
                            dataIndex: virtualRow.index,
                            style: {
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                minHeight: `${MIN_OPTION_HEIGHT}px`,
                                transform: `translateY(${virtualRow.start}px)`,
                            },
                            classes: optionItemClasses,
                            action: handleSelect,
                            disabled: false,
                            selected: valueSelected,
                        })}
                    </Fragment>
                );
            })}
        </InputAutocompleteSimpleLayout>
    );
};

export default InputAutoCompleteSimpleWithAccessors;
